/**
 * The component vocabulary. Everything visual in the app is built from these, which
 * is what keeps the instrument-panel idiom consistent across fifteen pages.
 */

import {
  useEffect,
  useRef,
  type ButtonHTMLAttributes,
  type ReactNode,
  type RefObject,
  type SelectHTMLAttributes,
} from 'react'

import { ApiError } from '@/shared/api/client'

// --- text ---------------------------------------------------------------------

/** The numbered datasheet label: `§03 / SEATS`. */
export function Label({ index, children }: { index?: string; children: ReactNode }) {
  return (
    <div className="label">
      {index && <span className="label__index">§{index}</span>}
      <span>{children}</span>
    </div>
  )
}

/** Tabular figure. Used for every number so columns align. */
export function Fig({ children }: { children: ReactNode }) {
  return <span className="fig">{children}</span>
}

// --- buttons ------------------------------------------------------------------

type ButtonVariant = 'default' | 'primary' | 'ghost' | 'danger'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: 'sm' | 'md' | 'lg'
  block?: boolean
  loading?: boolean
}

export function Button({
  variant = 'default',
  size = 'md',
  block = false,
  loading = false,
  disabled,
  children,
  className = '',
  ...rest
}: ButtonProps) {
  const classes = [
    'btn',
    variant !== 'default' ? `btn--${variant}` : '',
    size !== 'md' ? `btn--${size}` : '',
    block ? 'btn--block' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button className={classes} disabled={disabled ?? loading} {...rest}>
      {loading && <span className="spinner" aria-hidden="true" />}
      {children}
    </button>
  )
}

// --- containers ---------------------------------------------------------------

export function Panel({
  title,
  index,
  actions,
  footer,
  tone,
  children,
  padded = true,
}: {
  title?: ReactNode
  index?: string
  actions?: ReactNode
  footer?: ReactNode
  tone?: 'signal' | 'amber' | 'coral'
  children: ReactNode
  padded?: boolean
}) {
  const classes = ['panel', 'panel--flush', tone ? `panel--${tone}` : ''].filter(Boolean).join(' ')

  return (
    <section className={classes}>
      {(title || actions) && (
        <header className="panel__head">
          {title ? <Label index={index}>{title}</Label> : <span />}
          {actions && <div className="row">{actions}</div>}
        </header>
      )}
      <div className={padded ? 'panel__body' : ''}>{children}</div>
      {footer && <div className="panel__foot">{footer}</div>}
    </section>
  )
}

// --- state --------------------------------------------------------------------

export function Badge({
  tone,
  children,
  dot = false,
}: {
  tone?: 'signal' | 'amber' | 'coral'
  children: ReactNode
  dot?: boolean
}) {
  return (
    <span className={['badge', tone ? `badge--${tone}` : ''].filter(Boolean).join(' ')}>
      {dot && <span className="badge__dot" aria-hidden="true" />}
      {children}
    </span>
  )
}

/**
 * Maps a subscription status to a tone.
 *
 * Amber for anything time-bounded, coral for anything that has stopped working,
 * signal for anything healthy. Keeping the mapping in one function means a status
 * badge reads the same on every page.
 */
export function StatusBadge({ status }: { status?: string }) {
  if (!status) return <Badge>unknown</Badge>

  const tone: 'signal' | 'amber' | 'coral' | undefined = (() => {
    switch (status) {
      case 'active':
        return 'signal'
      case 'grace_payment':
      case 'canceled':
        return 'amber'
      case 'suspended':
      case 'expired':
      case 'revoked':
        return 'coral'
      default:
        return undefined
    }
  })()

  return (
    <Badge tone={tone} dot>
      {status.replace(/_/g, ' ')}
    </Badge>
  )
}

export function Notice({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'ok' | 'warn' | 'error'
  children: ReactNode
}) {
  const suffix = tone === 'info' ? '' : ` notice--${tone}`
  return (
    <div className={`notice${suffix}`} role={tone === 'error' ? 'alert' : 'status'}>
      {children}
    </div>
  )
}

/**
 * Renders an error the way the person can act on it.
 *
 * The backend writes customer-facing text for 4xx, so it is shown verbatim; 5xx
 * already arrives as a generic apology. The request id is appended because it is the
 * one thing support needs and the one thing a customer cannot reconstruct.
 */
export function ErrorNotice({ error }: { error: unknown }) {
  if (!error) return null

  if (error instanceof ApiError) {
    return (
      <Notice tone={error.isConflict ? 'warn' : 'error'}>
        <span>
          {error.message}
          {error.requestId && (
            <>
              {' '}
              <span className="mono-note">({error.requestId})</span>
            </>
          )}
        </span>
      </Notice>
    )
  }

  return <Notice tone="error">{error instanceof Error ? error.message : 'Unexpected error.'}</Notice>
}

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="empty">
      <span className="row" style={{ justifyContent: 'center' }}>
        <span className="spinner" aria-hidden="true" />
        {label}
      </span>
    </div>
  )
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>
}

// --- readouts -----------------------------------------------------------------

export function Readout({
  value,
  unit,
  caption,
  tone,
}: {
  value: ReactNode
  unit?: string
  caption: string
  tone?: 'signal' | 'amber'
}) {
  return (
    <div className="readout">
      <div className={['readout__value', tone ? `readout__value--${tone}` : ''].filter(Boolean).join(' ')}>
        {value}
        {unit && <span className="readout__unit">{unit}</span>}
      </div>
      <div className="readout__caption">{caption}</div>
    </div>
  )
}

/**
 * Segmented countdown meter.
 *
 * One segment per day remaining, so "3 days left" is legible without reading the
 * number. Turns amber under a third remaining and coral in the last two days — the
 * colour change is the nudge, not a modal.
 */
export function Meter({ total, remaining }: { total: number; remaining: number }) {
  const filled = Math.max(0, Math.min(total, remaining))
  const tone = remaining <= 2 ? 'crit' : remaining <= Math.ceil(total / 3) ? 'warn' : 'on'

  return (
    <div
      className="meter"
      role="meter"
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={filled}
      aria-label={`${filled} of ${total} days remaining`}
    >
      {Array.from({ length: total }, (_, index) => (
        <span
          key={index}
          className={`meter__seg${index < filled ? ` meter__seg--${tone}` : ''}`}
          aria-hidden="true"
        />
      ))}
    </div>
  )
}

// --- forms --------------------------------------------------------------------

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string
  hint?: string
  error?: string
  children: ReactNode
}) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      {children}
      {hint && !error && <span className="field__hint">{hint}</span>}
      {error && <span className="field__error">{error}</span>}
    </label>
  )
}

export function Select({
  options,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { options: Array<{ value: string; label: string }> }) {
  return (
    <select className="select" {...rest}>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  )
}

// --- modal --------------------------------------------------------------------

/**
 * A modal that carries content rather than a question.
 *
 * Separate from `ConfirmDialog` on purpose: that one exists to slow somebody down
 * before an irreversible action and its footer commits something. This one only ever
 * shows content, so the whole surface is safe to focus and the first Tab lands on the
 * close button.
 *
 * `wide` takes the width a table needs. Escape closes, the backdrop closes, and the
 * page behind is scroll-locked while it is open: a long table inside a scrollable
 * overlay on top of a scrollable page is three scroll contexts fighting, and the
 * reader loses their place in whichever one the browser picks.
 */
/**
 * What both dialogs do while open: hold focus, close on Escape, and optionally lock the
 * page behind them.
 *
 * It runs once per open. It used to re-run whenever the close callback changed, and
 * every caller passes one inline, so each render — each keystroke in a field inside the
 * dialog — pulled focus off the field and back onto the container (#914). The callback
 * is read from a ref instead, so Escape always calls the current one.
 *
 * Focus moves to the container only if nothing inside the dialog already has it, so an
 * input with `autoFocus` keeps it. It has to leave the page behind either way, or
 * Escape never reaches this handler.
 */
function useDialog(
  dialogRef: RefObject<HTMLDivElement | null>,
  onClose: () => void,
  { lockScroll }: { lockScroll: boolean },
) {
  const close = useRef(onClose)
  useEffect(() => {
    close.current = onClose
  })

  useEffect(() => {
    const dialog = dialogRef.current
    if (dialog && !dialog.contains(document.activeElement)) dialog.focus()

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close.current()
    }
    document.addEventListener('keydown', onKey)

    // Restored rather than cleared: another overlay may have set it, and clearing it
    // here would unlock the page underneath one that is still open.
    const previous = document.body.style.overflow
    if (lockScroll) document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', onKey)
      if (lockScroll) document.body.style.overflow = previous
    }
  }, [dialogRef, lockScroll])
}

export function Modal({
  title,
  subtitle,
  children,
  onClose,
  wide = false,
  toolbar,
  footer,
}: {
  title: string
  subtitle?: ReactNode
  children: ReactNode
  onClose: () => void
  wide?: boolean
  /** Sits between the head and the scrolling body, and does not scroll with it. */
  toolbar?: ReactNode
  footer?: ReactNode
}) {
  const dialogRef = useRef<HTMLDivElement>(null)
  useDialog(dialogRef, onClose, { lockScroll: true })

  return (
    <div className="overlay" onClick={onClose} role="presentation">
      <div
        ref={dialogRef}
        className={['modal', wide ? 'modal--wide' : ''].filter(Boolean).join(' ')}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="panel__head">
          <div className="stack" style={{ ['--gap' as string]: 'var(--s2)' }}>
            <Label>{title}</Label>
            {subtitle && (
              <p className="mono-note" style={{ margin: 0 }}>
                {subtitle}
              </p>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </header>
        {toolbar}
        <div className="modal__body">{children}</div>
        {footer && <div className="panel__foot">{footer}</div>}
      </div>
    </div>
  )
}

/**
 * A confirmation dialog for irreversible actions (blueprint §8.1).
 *
 * Escape closes, focus moves to the dialog on open, and the destructive button is
 * never the default focus target — an accidental Enter must not revoke somebody's
 * access.
 */
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  onConfirm,
  onCancel,
  pending = false,
  danger = false,
  confirmDisabled = false,
}: {
  title: string
  body: ReactNode
  confirmLabel: string
  onConfirm: () => void
  onCancel: () => void
  pending?: boolean
  danger?: boolean
  /** Holds the destructive button shut until a typed confirmation matches. */
  confirmDisabled?: boolean
}) {
  const dialogRef = useRef<HTMLDivElement>(null)
  // Focus lands on the dialog itself rather than a control inside it: the destructive
  // button must never be the default target, or an in-flight Enter keystroke confirms
  // the action the person opened the dialog to think about. A field with autoFocus
  // keeps its focus; see useDialog.
  useDialog(dialogRef, onCancel, { lockScroll: false })

  return (
    <div className="overlay" onClick={onCancel} role="presentation">
      <div
        ref={dialogRef}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="panel__head">
          <Label>{title}</Label>
        </header>
        <div className="panel__body stack">{body}</div>
        <div className="panel__foot row row--between">
          <Button variant="ghost" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            onClick={onConfirm}
            loading={pending}
            disabled={confirmDisabled}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}

// --- formatting ---------------------------------------------------------------

export function formatDate(iso?: string | null): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' })
}

export function formatDateTime(iso?: string | null): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** "3 days ago", "in 6 days" — relative time reads better than a date for
 *  anything a person is tracking rather than recording. */
export function formatRelative(iso?: string | null): string {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return '—'

  const deltaMs = then - Date.now()
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['day', 86_400_000],
    ['hour', 3_600_000],
    ['minute', 60_000],
  ]
  for (const [unit, ms] of units) {
    if (Math.abs(deltaMs) >= ms || unit === 'minute') {
      return formatter.format(Math.round(deltaMs / ms), unit)
    }
  }
  return 'just now'
}

/**
 * Formats an amount of cents as a price.
 *
 * Cents are shown only when there are any: `$10` and `$102` stay clean, while the
 * per-month equivalent of an annual plan ($8.50) keeps its decimals. Rounding that
 * one to the nearest dollar — which is what this did — advertised $9/month against
 * a $102 charge, and twelve of $9 is not $102.
 */
export function formatPrice(cents: number): string {
  const whole = cents % 100 === 0
  return `$${(cents / 100).toLocaleString(undefined, {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  })}`
}

export function platformLabel(platform: string, arch: string): string {
  const names: Record<string, string> = { darwin: 'macOS', linux: 'Linux', windows: 'Windows' }
  const archNames: Record<string, string> = { arm64: 'arm64', x86_64: 'x86_64', amd64: 'x86_64' }
  return `${names[platform] ?? platform} · ${archNames[arch] ?? arch}`
}
